import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import './Return.css';

function Return() {
  const navigate        = useNavigate();
  const { labelId }     = useParams();
  const isEditing       = Boolean(labelId);

  const [step, setStep]               = useState(0);
  const [articles, setArticles]       = useState([]);
  const [quantities, setQuantities]   = useState({});
  const [paymentTypes,  setPaymentTypes]  = useState([]);
  const [paymentTypeId, setPaymentTypeId] = useState('');
  const [loading, setLoading]         = useState(true);
  const [submitting, setSubmitting]   = useState(false);
  const [savedLabelId, setSavedLabelId] = useState(labelId ? Number(labelId) : null);
  const [error, setError]             = useState('');
  const [maxQty, setMaxQty]           = useState(null);
  const [etransferEmail, setEtransferEmail]       = useState(null);
  const [payoutThreshold, setPayoutThreshold]     = useState(null);
  const [etransferEmailInput, setEtransferEmailInput] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const requests = [
          axios.get('/api/articles'),
          axios.get('/api/payment-types'),
          axios.get('/api/profile'),
          axios.get('/api/config'),
        ];
        if (isEditing) requests.push(axios.get(`/api/labels/${labelId}`));
        const results = await Promise.all(requests);

        setArticles(results[0].data);
        setPaymentTypes(results[1].data);
        const profile = results[2].data;
        setPaymentTypeId(String(profile.payment_type_id ?? ''));
        setEtransferEmail(profile.etransfer_email || null);
        setPayoutThreshold(profile.payout_threshold ?? null);

        const configData = results[3].data;
        if (configData?.bag_max_quantity?.value_num != null) {
          setMaxQty(Number(configData.bag_max_quantity.value_num));
        }

        if (isEditing && results[4]) {
          const labelData = results[4].data;
          if (labelData.payment_type_id != null)
            setPaymentTypeId(String(labelData.payment_type_id));
          const initQty = {};
          labelData.items.forEach(item => { initQty[item.article_id] = item.qtty; });
          setQuantities(initQty);
        }
      } catch {
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [labelId, isEditing]);

  const setQty = (articleId, raw) => {
    const qty = Math.max(0, parseInt(raw) || 0);
    setQuantities(prev => {
      if (qty === 0) { const next = { ...prev }; delete next[articleId]; return next; }
      if (maxQty !== null) {
        const currentTotal = Object.values(prev).reduce((s, v) => s + v, 0);
        const currentForThis = prev[articleId] || 0;
        const headroom = maxQty - (currentTotal - currentForThis);
        const clamped = Math.min(qty, Math.max(0, headroom));
        if (clamped === 0) return prev;
        return { ...prev, [articleId]: clamped };
      }
      return { ...prev, [articleId]: qty };
    });
  };

  const selected = articles
    .filter(a => (quantities[a.article_id] || 0) > 0)
    .map(a => ({ ...a, qty: quantities[a.article_id] }));

  const total    = selected.reduce((sum, a) => sum + Number(a.price) * a.qty, 0);
  const totalQty = selected.reduce((sum, a) => sum + a.qty, 0);
  const atMax    = maxQty !== null && totalQty >= maxQty;

  const handleSubmit = async () => {
    if (String(paymentTypeId) === '1' && !etransferEmail && !etransferEmailInput.trim()) {
      setError('Please enter your e-Transfer email address to continue.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const labelPayload = {
        items: selected.map(a => ({ article_id: a.article_id, qtty: a.qty })),
        payment_type_id: paymentTypeId !== '' ? Number(paymentTypeId) : null,
      };
      const requests = [
        isEditing
          ? axios.put(`/api/labels/${labelId}`, labelPayload)
          : axios.post('/api/labels', labelPayload),
      ];
      const trimmedEmail = etransferEmailInput.trim();
      if (trimmedEmail) {
        requests.push(axios.put('/api/profile', {
          etransfer_email: trimmedEmail,
          payout_threshold: payoutThreshold,
          payment_type_id: paymentTypeId !== '' ? Number(paymentTypeId) : null,
        }));
      }
      const results = await Promise.all(requests);
      const labelResult = results[0];
      if (isEditing) {
        setSavedLabelId(Number(labelId));
      } else {
        setSavedLabelId(labelResult.data.label_id);
      }
      if (trimmedEmail) setEtransferEmail(trimmedEmail);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit return. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(0);
    setQuantities({});
    setSavedLabelId(null);
    setError('');
  };

  if (loading) {
    return <div className="return-page"><p>Loading…</p></div>;
  }

  // ── Step 0: Article selection ─────────────────────────────────────────────
  if (step === 0) {
    return (
      <div className="return-page">
        <h2 className="return-title return-title--standalone">
          {isEditing ? `Edit Empty Return #${labelId}` : 'Start a New Empty Return'}
        </h2>

        {error && <p className="return-error">{error}</p>}

        {atMax && (
          <p className="return-max-notice">
            Maximum of {maxQty} containers per return reached.
          </p>
        )}

        {articles.length === 0 ? (
          <p>No returnable articles available.</p>
        ) : (
          <div className="article-list">
            {articles.map(article => {
              const qty = quantities[article.article_id] || 0;
              const canIncrease = !atMax || qty < (maxQty - (totalQty - qty));
              return (
                <div key={article.article_id} className="article-row">
                  <div className="article-info">
                    <span className="article-desc">{article.description}</span>
                    <span className="article-price">${Number(article.price).toFixed(2)} / unit</span>
                  </div>
                  <div className="quantity-wrapper">
                    <button className="stepper-btn decrease"
                      onClick={() => setQty(article.article_id, qty - 1)}>
                      −
                    </button>
                    <input
                      type="number"
                      min="0"
                      className="quantity-input"
                      value={qty || ''}
                      onChange={e => setQty(article.article_id, e.target.value)}
                      placeholder="0"
                    />
                    <button
                      className="stepper-btn increase"
                      onClick={() => setQty(article.article_id, qty + 1)}
                      disabled={!canIncrease}
                    >
                      +
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="return-info-blurb">
          <p className="return-etransfer-caption">
            Returning multiple bags? Create a separate Empty Return for each bag.
          </p>
        </div>

        {selected.length > 0 && (
          <div className="return-footer">
            <div className="return-total-bar">
              <span>{selected.length} container type{selected.length !== 1 ? 's' : ''} selected</span>
              <span className="return-total">${total.toFixed(2)}</span>
            </div>
            <button className="actionButton" onClick={() => setStep(1)}>
              Review Return
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Step 1: Review ────────────────────────────────────────────────────────
  if (step === 1) {
    return (
      <div className="return-page">
        <div className="return-header">
          <button className="return-back-btn" onClick={() => setStep(0)} aria-label="Back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
          <h2 className="return-title">Review Your Empty Return</h2>
        </div>

        <table>
          <thead>
            <tr>
              <th>Container</th>
              <th>Qty</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {selected.map(a => (
              <tr key={a.article_id}>
                <td>{a.description}</td>
                <td>{a.qty}</td>
                <td>${(Number(a.price) * a.qty).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2}><strong>Total</strong></td>
              <td><strong>${total.toFixed(2)}</strong></td>
            </tr>
          </tfoot>
        </table>

        {paymentTypes.length > 0 && (
          <div className="return-payment-type">
            <label htmlFor="payment-type">Payment Method</label>
            <select
              id="payment-type"
              value={paymentTypeId}
              onChange={e => setPaymentTypeId(e.target.value)}
            >
              <option value="">— Select —</option>
              {paymentTypes.map(pt => (
                <option key={pt.payment_type_id} value={pt.payment_type_id}>
                  {pt.description}
                </option>
              ))}
            </select>
          </div>
        )}

        {String(paymentTypeId) === '1' && !etransferEmail && (
          <div className="return-etransfer-prompt">
            <label htmlFor="etransfer-email">e-Transfer Email</label>
            <input
              id="etransfer-email"
              type="email"
              placeholder="you@example.com"
              value={etransferEmailInput}
              onChange={e => setEtransferEmailInput(e.target.value)}
            />
            <p className="return-etransfer-caption">
              We will only ask for this once. Your e-Transfer email can be updated at any time on the Profile page.
            </p>
          </div>
        )}

        {error && <p className="return-error">{error}</p>}

        <div className="return-footer">
          <div className="return-total-bar">
            <span>Total</span>
            <span className="return-total">${total.toFixed(2)}</span>
          </div>
          <button className="actionButton" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Submitting…' : isEditing ? 'Update Return' : 'Submit Return'}
          </button>
        </div>
      </div>
    );
  }

  // ── Step 2: Confirmation ──────────────────────────────────────────────────
  return (
    <div className="return-page">
      <div className="return-confirm">
        <h2>{isEditing ? 'Empty Return Updated' : 'Empty Return Submitted'}</h2>
        <p className="label-id">Label {savedLabelId}</p>
        <p>
          {isEditing
            ? 'Your return has been updated successfully.'
            : <>Your empty return is ready to be dropped off. Visit a participating Drop & Go location and scan your  <Link to="/qr-code">QR Code</Link> to print your return label. Each bag must have a printed label attached before it can be dropped off.</>}
        </p>
        <div className="return-actions return-actions--center">
          {isEditing ? (
            <button className="actionButton" onClick={() => navigate('/')}>Done</button>
          ) : (
            <button className="actionButton" onClick={reset}>New Empty Return</button>
          )}
          <button onClick={() => navigate('/history')}>Transaction History</button>
        </div>
      </div>
    </div>
  );
}

export default Return;
